<template>
  <div class="table--draggable">
    <div class="thead" data-content="Pos.">
      <div class="th">
        <span>{{ $t('containers.containerGroup') }}</span>
      </div>
      <div class="th">
        <span>{{ $t('routes.routeEditor.referenceDate') }}</span>
      </div>
      <div class="th">
        <span>{{ $t('containerGroups.lastEmptying') }}</span>
      </div>
      <div class="th">
        <span>{{ $t('routes.routeEditor.emptyingInterval') }}</span>
      </div>
      <div class="th">
        <span class="visually-hidden">{{ $t('routes.routeEditor.containersCount') }}</span>
        <span class="material-icons">delete</span>
      </div>
      <div class="th"></div>
    </div>
    <div class="tbody-wrapper">
      <draggable
        handle=".handle"
        v-model="list"
        @change="onChange"
        :group="{ name: 'containerGroupsWithPosition', pull: false, put: true }"
        :class="{ 'tbody--empty': list.length === 0 }"
        :force-fallback="false"
        :scroll-sensitivity="200"
        :animation="300"
        :data-content="$t('routes.routeEditor.noGroupsWithPosition')"
      >
        <div
          :data-content="index + 1"
          v-for="(item, index) in list"
          class="tr--draggable"
          :key="item.uuid"
          draggable="true"
        >
          <div class="table-cell">
            <Link :to="`/company/container_groups/${item.uuid}`">{{ item.name }}</Link>
          </div>
          <div class="table-cell table-cell--date">
            <span>{{ formattedDate(item.reference_date) }}</span>
          </div>
          <div class="table-cell table-cell--date">
            <span>{{ formattedDate(item.empty_date) }}</span>
          </div>
          <div class="table-cell table-cell--num">
            <span>{{ item.repeat_interval }}</span>
          </div>
          <div class="table-cell table-cell--num">
            <span>{{ item.containers_count }}</span>
          </div>
          <div class="table-cell" :title="$t('routes.routeEditor.dragToMove')">
            <span class="material-icons handle">drag_indicator</span>
          </div>
        </div>
      </draggable>
      <draggable
        handle=".handle"
        v-model="listWithoutPosition"
        :group="{ name: 'containerGroupsWithoutPosition', pull: true, put: false }"
        :sort="false"
        :animation="300"
        :scroll-sensitivity="200"
        :force-fallback="false"
      >
        <div
          data-content="-"
          v-for="item in listWithoutPosition"
          class="tr--draggable without-position"
          :key="item.uuid"
          draggable="true"
        >
          <div class="table-cell">
            <Link :to="`/company/container_groups/${item.uuid}`">{{ item.name }}</Link>
          </div>
          <div class="table-cell table-cell--date">
            <span>{{ formattedDate(item.reference_date) }}</span>
          </div>
          <div class="table-cell table-cell--date">
            <span>{{ formattedDate(item.empty_date) }}</span>
          </div>
          <div class="table-cell table-cell--num">
            <span>{{ item.repeat_interval }}</span>
          </div>
          <div class="table-cell table-cell--num">
            <span>{{ item.containers_count }}</span>
          </div>
          <div class="table-cell" :title="$t('routes.routeEditor.dragToMove')">
            <span class="material-icons handle">drag_indicator</span>
          </div>
        </div>
      </draggable>
    </div>
  </div>
</template>
<script>
import { partition } from 'lodash'
import draggable from 'vuedraggable'

import dateUtils from '@/components/mixins/dateUtils'

export default {
  name: 'DraggableTable',
  components: {
    draggable,
  },
  mixins: [dateUtils],
  emits: ['dragged-and-dropped'],
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    list: [],
    listWithoutPosition: [],
  }),
  watch: {
    items: {
      handler() {
        this.createLists(this.items)
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    onChange(event) {
      let element
      // position starts at 1, so we need to offset zero-based indexing
      if (event.moved) {
        const newPosition = event.moved.newIndex + 1
        element = {
          container_group_uuid: event.moved.element.uuid,
          position: newPosition,
        }
      }
      if (event.added) {
        const newPosition = event.added.newIndex + 1
        element = {
          container_group_uuid: event.added.element.uuid,
          position: newPosition,
        }
      }
      this.$emit('dragged-and-dropped', element)
    },
    createLists(items) {
      // prettier-ignore
      [this.listWithoutPosition, this.list] = partition(items, { position: null })
    },
  },
}
</script>

<style lang="scss" scoped>
.handle {
  cursor: grab;
}

.table--draggable {
  position: relative;
  height: 100%;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12);

  .thead,
  .tr--draggable {
    position: relative;
    width: 100%;
    display: grid;
    grid-template-columns: minmax(150px, 2.33fr) repeat(2, minmax(150px, 1.33fr)) minmax(125px, 1.33fr) repeat(2, 60px);
    border-bottom: 1px solid #f1f1f1;
    padding-left: 3rem;
  }

  .tr--draggable:last-of-type {
    border-bottom: none;
  }

  .thead {
    color: #5c5c5c;
    background-color: #f1f1f1;
  }

  .thead::before,
  .tr--draggable::before {
    content: attr(data-content);
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 3rem;
    position: absolute;
    top: 0;
    left: 0;
    background-color: var(--binity-blue);
    color: #fff;
  }

  .tbody-wrapper {
    height: 100%;
    width: calc(100% + 3rem);
    padding-left: 3rem;
    margin-left: -3rem;
    overflow-y: scroll;
    background: linear-gradient(white 33%, rgba(255, 255, 255, 0)),
      linear-gradient(rgba(255, 255, 255, 0), white 66%) 0 100%,
      radial-gradient(farthest-side at 50% 0, rgba(34, 34, 34, 0.5), rgba(0, 0, 0, 0)),
      radial-gradient(farthest-side at 50% 100%, rgba(34, 34, 34, 0.5), rgba(0, 0, 0, 0)) 0 100%;
    background-color: transparent;
    background-repeat: no-repeat;
    background-attachment: local, local, scroll, scroll;
    background-size: 100% 27px, 100% 27px, 100% 9px, 100% 9px;
    background-position-x: 3rem, 3rem, 3rem, 3rem;
  }

  .tr--draggable.without-position::before {
    opacity: 0.5;
  }

  .tr--draggable.without-position {
    .table-cell {
      opacity: 0.75;
    }
  }

  .th,
  .table-cell {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: nowrap;
    padding: 0.75rem;
  }

  .th:first-of-type,
  .table-cell:first-of-type {
    justify-content: flex-start;
  }

  .th > *,
  .table-cell > * {
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .table-cell {
    a {
      display: inline-block;
      width: 100%;
      text-decoration: underline;
      color: var(--primary-font-color);
      font-weight: 600;
      &:hover {
        color: #000;
      }
    }
  }

  .tbody--empty {
    min-height: 4rem;
    position: relative;
  }

  .tbody--empty::before {
    content: attr(data-content);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 0 0.75rem;
    position: absolute;
    top: 0;
    left: -3rem;
    right: 0;
    border: 1px dashed #5c5c5c80;
    color: #5c5c5c;
  }

  .table-cell--date,
  .table-cell--num {
    font-feature-settings: 'tnum';
    font-variant-numeric: tabular-nums;
    text-align: center;
  }
}
</style>
